import { Badge } from "@/components/ui/badge";
import type { ResultsLookup, ResultsLookupReason } from "../../shared/api/website";

/**
 * Trạng thái tra cứu kết quả trên website, dùng chung cho màn Website của phòng khám và màn quản
 * trị nền tảng (#19191) — hai nơi cùng đổi một ô tích nên phải nói cùng một câu.
 */

export const RESULTS_LOOKUP_REASON: Record<ResultsLookupReason, string> = {
  NOT_SUPPORTED_BY_HIS:
    "Phòng khám đang dùng phần mềm HIS bên ngoài, website chưa đọc được kết quả từ đó. Website sẽ hướng dẫn bệnh nhân gọi phòng khám.",
  DISABLED_BY_CLINIC: "Phòng khám chưa cho phép, website sẽ không hiện mục tra cứu kết quả.",
  OTP_ACTION_DISABLED:
    "Hành động “Mã xác thực” đang tắt ở Cài đặt › Thông báo, website đang ẩn mục tra cứu. Bật lại hành động đó để mở lại.",
  MESSAGING_NOT_CONFIGURED:
    "Chưa gửi được mã xác thực (OTP) tới điện thoại bệnh nhân: phòng khám chưa có kênh nhắn tin Addy Message (Zalo ZBS/SMS). Website sẽ chưa hiện mục tra cứu cho tới khi có kênh gửi.",
  OTP_TEMPLATE_NOT_READY:
    "Mẫu OTP không còn dùng được, website đang ẩn mục tra cứu. Chọn lại mẫu OTP để mở lại.",
  OTP_CHECK_UNAVAILABLE:
    "Không kiểm tra được mẫu OTP lúc này (không kết nối được Addy Message). Website tạm ẩn mục tra cứu.",
};

/** Câu nhắc trước khi bỏ tích: tắt là cắt luôn các phiên đang mở, không đợi hết 15 phút. */
export const RESULTS_LOOKUP_OFF_WARNING =
  "Bệnh nhân đang xem kết quả sẽ bị thoát ngay.";

export type ResultsLookupState = "OPEN" | "OFF" | "NOT_READY";

/**
 * 🟢 Đang mở — bật và website đang hiện mục tra cứu. ⚪ Đã tắt — ô chưa tích. 🟠 Chưa sẵn sàng —
 * đã tích (hoặc HIS ngoài) nhưng website chưa tra cứu được, kèm lý do.
 */
export function resultsLookupState(d: ResultsLookup): ResultsLookupState {
  if (d.examResults.available) return "OPEN";
  if (d.examResults.reason === "NOT_SUPPORTED_BY_HIS") return "NOT_READY";
  return d.enabled ? "NOT_READY" : "OFF";
}

const LABEL: Record<ResultsLookupState, string> = {
  OPEN: "Đang mở trên website",
  OFF: "Đã tắt",
  NOT_READY: "Chưa sẵn sàng",
};

const VARIANT: Record<ResultsLookupState, "success" | "muted" | "warning"> = {
  OPEN: "success",
  OFF: "muted",
  NOT_READY: "warning",
};

export function ResultsLookupStatusBadge({ data }: { data: ResultsLookup }) {
  const s = resultsLookupState(data);
  return (
    <Badge variant={VARIANT[s]} className="w-fit gap-1.5">
      <span aria-hidden>●</span>
      {LABEL[s]}
    </Badge>
  );
}

/** Lý do khi chưa sẵn sàng; "Đã tắt" tự nói đủ nên không lặp lại câu DISABLED_BY_CLINIC. */
export function resultsLookupReasonText(d: ResultsLookup): string | null {
  if (resultsLookupState(d) !== "NOT_READY") return null;
  const r = d.examResults.reason;
  return r ? (RESULTS_LOOKUP_REASON[r] ?? "Website chưa hiện mục tra cứu kết quả.") : null;
}

/** One reason the lookup cannot be turned on yet, with where to fix it (#19614). */
export type TurnOnBlocker = {
  key: string;
  text: string;
  /** Per-channel detail, already in Vietnamese ("SMS: chưa chọn mẫu."). */
  details?: string[];
  fix?: { to: string; label: string };
};

const OTP_SETTINGS = "/settings/notifications#PATIENT_OTP";

/**
 * Everything that stands between the clinic and ticking "Cho phép bệnh nhân tra cứu…" — every
 * unmet condition at once, not the first one per save attempt. Read from `otp`, the OTP half the
 * server answers whatever the switch says, i.e. the very check `PUT` refuses on (422
 * `otp_not_ready`), so the screen never offers what the save would refuse. An older API without
 * `otp` blocks nothing here; the server still refuses.
 */
export function turnOnBlockers(d: ResultsLookup): TurnOnBlocker[] {
  const otp = d.otp;
  if (!otp || otp.available) return [];
  if (otp.reason === "OTP_CHECK_UNAVAILABLE") {
    return [
      {
        key: "unavailable",
        text: "Chưa kiểm tra được mẫu OTP (không kết nối được Addy Message). Thử lại sau ít phút.",
      },
    ];
  }
  const out: TurnOnBlocker[] = [];
  if (otp.otpActionEnabled === false) {
    out.push({
      key: "action",
      text: "Bật hành động “Mã xác thực” ở Cài đặt › Thông báo.",
      fix: { to: OTP_SETTINGS, label: "Bật Mã xác thực" },
    });
  }
  if (otp.messagingConfigured === false) {
    out.push({
      key: "messaging",
      text: "Kết nối kênh nhắn tin Addy Message (Zalo OA hoặc SMS) để gửi được mã.",
      fix: { to: "/messaging", label: "Mở Addy Message" },
    });
  } else {
    const channels = otp.otpChannels ?? [];
    if (!channels.some((c) => c.ready)) {
      out.push({
        key: "template",
        text: "Chọn ít nhất một mẫu OTP hợp lệ cho Zalo hoặc SMS.",
        details: channels.filter((c) => c.problem).map((c) => c.problem as string),
        fix: { to: OTP_SETTINGS, label: "Chọn mẫu OTP" },
      });
    }
  }
  if (out.length === 0) {
    // A reason the list above does not name (a newer server) — say so rather than show nothing.
    out.push({
      key: "other",
      text: RESULTS_LOOKUP_REASON[otp.reason ?? "OTP_TEMPLATE_NOT_READY"] ?? "Mã OTP chưa sẵn sàng.",
      fix: { to: OTP_SETTINGS, label: "Mở cài đặt Mã xác thực" },
    });
  }
  return out;
}
